import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { TrendingUp } from 'lucide-react';

const GROUP_OPTIONS = [
  ['day', 'Day'],
  ['hour', 'Hour'],
  ['weekday', 'Weekday'],
  ['month', 'Month'],
];
const DAYS_OPTIONS = [7, 30, 90, 365];

function fmtNum(v) {
  return Number(v || 0).toLocaleString();
}
function fmtMoney(v) {
  // Prefix with the currency sign so Trends revenue reads like money ("$250.00")
  // and matches the rest of the app, instead of a bare "250.00".
  return (
    '$' +
    Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

export default function Trends() {
  const { t } = useT();
  const [basis, setBasis] = useState('trends');
  const [groupBy, setGroupBy] = useState('day');
  const [days, setDays] = useState(30);
  const [campaignId, setCampaignId] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(API + '/campaigns')
      .then((r) => setCampaigns(Array.isArray(r.data) ? r.data : (r.data && r.data.campaigns) || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint =
        basis === 'conversion_date' ? '/analytics/conversions-by-date' : '/analytics/trends';
      const params = { days: days, group_by: groupBy };
      if (campaignId && campaignId !== 'all') params.campaign_id = campaignId;
      const res = await axios.get(API + endpoint, { params });
      setSeries((res.data && res.data.series) || []);
    } catch (err) {
      console.error('Error loading trends:', err);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  }, [basis, days, groupBy, campaignId]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = series.reduce(
    (acc, row) => ({
      clicks: acc.clicks + (row.clicks || 0),
      unique_clicks: acc.unique_clicks + (row.unique_clicks || 0),
      conversions: acc.conversions + (row.conversions || 0),
      revenue: acc.revenue + (row.revenue || 0),
    }),
    { clicks: 0, unique_clicks: 0, conversions: 0, revenue: 0 }
  );

  const isConvDate = basis === 'conversion_date';

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Analytics')}
        title={t('Trends')}
        subtitle={t('Key metrics over time buckets. Toggle to attribute conversions to their arrival date.')}
      />

      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">{t('Report')}</label>
              <div className="flex rounded-md border border-border overflow-hidden w-fit">
                <button
                  type="button"
                  onClick={() => setBasis('trends')}
                  className={
                    'px-3 py-1.5 text-sm transition-colors ' +
                    (!isConvDate
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {t('Traffic trends')}
                </button>
                <button
                  type="button"
                  onClick={() => setBasis('conversion_date')}
                  className={
                    'px-3 py-1.5 text-sm transition-colors ' +
                    (isConvDate
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {t('Conversion date')}
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">{t('Group by')}</label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_OPTIONS.map(([v, l]) => (
                    <SelectItem key={v} value={v}>{t(l)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">{t('Period')}</label>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OPTIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>{d + ' ' + t('days')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">{t('Campaign')}</label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('All campaigns')}</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="skeleton h-72 w-full"></div>
          ) : series.length === 0 ? (
            <div className="py-10">
              <XpnsrEmpty
                icon={TrendingUp}
                title={t('No data for this period')}
                subtitle={t('Adjust the period or campaign filter.')}
              />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="tA" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="rgb(var(--tint))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="rgb(var(--tint))" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="tB" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="rgb(var(--success))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="rgb(var(--success))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'rgb(var(--muted))' }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgb(var(--border))' }}
                />
                <YAxis
                  tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'rgb(var(--muted))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip
                  contentStyle={{
                    background: 'rgb(var(--surface))',
                    border: '1px solid rgb(var(--border))',
                    borderRadius: 4,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                  }}
                  labelStyle={{ color: 'rgb(var(--text))', fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {isConvDate ? (
                  <Area type="monotone" dataKey="conversions" stroke="rgb(var(--tint))" strokeWidth={2} fill="url(#tA)" name={t('Conversions')} />
                ) : (
                  <Area type="monotone" dataKey="clicks" stroke="rgb(var(--tint))" strokeWidth={2} fill="url(#tA)" name={t('Clicks')} />
                )}
                {isConvDate ? (
                  <Area type="monotone" dataKey="revenue" stroke="rgb(var(--success))" strokeWidth={2} fill="url(#tB)" name={t('Revenue')} />
                ) : (
                  <Area type="monotone" dataKey="conversions" stroke="rgb(var(--success))" strokeWidth={2} fill="url(#tB)" name={t('Conversions')} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {series.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('Breakdown')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Bucket')}</TableHead>
                  {!isConvDate ? <TableHead className="text-right">{t('Clicks')}</TableHead> : null}
                  {!isConvDate ? <TableHead className="text-right">{t('Unique')}</TableHead> : null}
                  <TableHead className="text-right">{t('Conversions')}</TableHead>
                  <TableHead className="text-right">{t('Revenue')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {series.map((row) => (
                  <TableRow key={row.bucket}>
                    <TableCell className="font-medium">{row.label}</TableCell>
                    {!isConvDate ? <TableCell className="text-right">{fmtNum(row.clicks)}</TableCell> : null}
                    {!isConvDate ? <TableCell className="text-right">{fmtNum(row.unique_clicks)}</TableCell> : null}
                    <TableCell className="text-right">{fmtNum(row.conversions)}</TableCell>
                    <TableCell className="text-right">{fmtMoney(row.revenue)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold">
                  <TableCell>{t('Total')}</TableCell>
                  {!isConvDate ? <TableCell className="text-right">{fmtNum(totals.clicks)}</TableCell> : null}
                  {!isConvDate ? <TableCell className="text-right">{fmtNum(totals.unique_clicks)}</TableCell> : null}
                  <TableCell className="text-right">{fmtNum(totals.conversions)}</TableCell>
                  <TableCell className="text-right">{fmtMoney(totals.revenue)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
